/**
 * Token lifecycle for the Curator.
 *
 * The problem this solves: login stored the access token in a cookie with a
 * one-day expiry and never touched it again. An Entra access token lives about
 * an hour, so for the rest of that day every request carried a dead token, and
 * nothing noticed — ProtectedRoute only checks whether MSAL still has an
 * account, and an account outlives its token. The user was neither refreshed
 * nor signed out; they simply kept working with credentials that had expired.
 *
 * So the cookie stops being the source of truth. MSAL is: it holds the refresh
 * token and renews silently, and every request now asks it for a token rather
 * than reading whatever was cached at login. The cookie is still written —
 * five call sites build their own Authorization header from it — but it is
 * kept in step with the real token and its expiry now matches the token's.
 *
 * Scopes are deliberately unchanged. The request interceptor sends the same
 * kind of token the app has always sent; only its freshness changes. Asking
 * for the API's own scope instead would alter the `aud` and `scp` claims the
 * rest-api-authorizer reads, and that belongs with the authorizer work, not
 * here.
 */

import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import axios from "axios";
import Cookies from "js-cookie";

import { msalConfig, loginRequest } from "./authConfig";

export const msalInstance = new PublicClientApplication(msalConfig);

// MSAL v3 refuses every other call until initialize() has resolved. MsalProvider
// does this for the React tree, but the axios interceptor below runs outside it
// and can fire first. Kept as one promise: initialize() is idempotent, and
// awaiting the same promise avoids a race between the two entry points.
const ready = msalInstance.initialize();

const TOKEN_COOKIE = "authToken";

function activeAccount() {
    return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
}

function storeToken(result) {
    // Expire the cookie with the token rather than a day later, so a stale
    // cookie cannot outlive what it holds.
    const options = result.expiresOn ? { expires: result.expiresOn } : undefined;
    Cookies.set(TOKEN_COOKIE, result.accessToken, options);
    return result.accessToken;
}

/** Drop the session and send the user to login, preserving where they were. */
export function endSession() {
    Cookies.remove(TOKEN_COOKIE);
    // Already there: the login page makes no API calls, so nothing should send
    // the user here twice, but a reload of /login would wipe what they typed.
    if (window.location.pathname === "/login") return;
    // App.js routes with BrowserRouter, so /login is a path. This used to go to
    // "/#/login", which BrowserRouter reads as "/": ProtectedRoute still saw
    // the MSAL account and rendered the app, its first request was refused
    // with the same dead token, and this ran again. The result was a reload
    // loop, seen as a blinking screen, once the 24-hour refresh token ran out.
    // Login reads `origin`, not `redirect`; config.loginLink sends `redirect`,
    // which only ever fed a message, never a route.
    const origin = `${window.location.pathname || ""}${window.location.search || ""}` || "/";
    window.location.assign(`/login?origin=${encodeURIComponent(origin)}`);
}

/**
 * A token that is valid right now, or null if the user has to sign in again.
 *
 * acquireTokenSilent returns the cached token while it is good and renews it
 * from the refresh token when it is not, so this is cheap to call per request.
 */
export async function getAccessToken() {
    await ready;
    const account = activeAccount();
    if (!account) return null;

    try {
        return storeToken(await msalInstance.acquireTokenSilent({ ...loginRequest, account }));
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            // The refresh token is gone or the tenant wants the user back —
            // no amount of retrying fixes this one.
            return null;
        }
        // Anything else (network, MSAL internals) is not proof the session is
        // over. Fall back to the cookie so a blip does not sign the user out;
        // if the token really has expired the API will say so.
        return Cookies.get(TOKEN_COOKIE) || null;
    }
}

/**
 * Attach the token lifecycle to every axios call in the app.
 *
 * Call sites keep building their own `Authorization` header from the cookie.
 * The request interceptor overwrites it, which is the point: one place decides
 * what a live token is, and no call site has to be changed to benefit.
 */
export function installAuthInterceptors() {
    axios.interceptors.request.use(async (config) => {
        const token = await getAccessToken();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error.response && error.response.status;
            // 401 is the honest answer. 403 is included because the
            // rest-api-authorizer returns a Deny policy for every failure path,
            // including an expired token, and API Gateway renders all of them
            // as 403 — so a dead session is indistinguishable from a permission
            // denial at this layer.
            if (status === 401 || status === 403) {
                // Only sign out when the session really is unrecoverable.
                // Otherwise this would eject a signed-in user who simply asked
                // for something they are not allowed to do.
                if ((await getAccessToken()) === null) {
                    endSession();
                }
            }
            return Promise.reject(error);
        },
    );
}
